import React, { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../api/client';
import EventFallbackPattern from './EventFallbackPattern';
import './RotatingImage.css';

interface RotatingImageProps {
  coverImage?: string | null;
  gallery?: string[] | null;
  /** Stable seed for the fallback pattern (e.g. String(course._id)). */
  seed: string;
  alt: string;
}

const IMAGE_CYCLE_MS = 4000;

/**
 * Resolve an upload path to a fully-qualified URL.
 * Replicated from EventMarquee's getImageUrl to avoid touching that component.
 */
const getImageUrl = (path: string | undefined | null): string | null => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/uploads')) return `${getApiBaseUrl()}${path}`;
  return path;
};

/**
 * RotatingImage – presents a course's imagery for cards/headers.
 *
 * Precedence:
 * 1. coverImage present       → single image
 * 2. gallery has ≥2 images    → crossfade through them
 * 3. gallery has exactly 1     → single image
 * 4. otherwise                → deterministic <EventFallbackPattern />
 *
 * The crossfade replicates EventMarquee's GalleryCycler (interval + opacity).
 */
const RotatingImage: React.FC<RotatingImageProps> = ({ coverImage, gallery, seed, alt }) => {
  const cover = getImageUrl(coverImage);
  const galleryUrls = (Array.isArray(gallery) ? gallery : [])
    .map((p) => getImageUrl(p))
    .filter((u): u is string => u !== null);

  const crossfade = !cover && galleryUrls.length >= 2;

  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!crossfade) return;
    const id = setInterval(() => {
      setCurrent((prev) => (prev + 1) % galleryUrls.length);
    }, IMAGE_CYCLE_MS);
    return () => clearInterval(id);
  }, [crossfade, galleryUrls.length]);

  if (cover) {
    return <img src={cover} alt={alt} className="rotating-image__single" loading="lazy" />;
  }

  if (crossfade) {
    return (
      <div className="rotating-image__gallery">
        {galleryUrls.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={i === current ? alt : ''}
            className="rotating-image__gallery-img"
            style={{ opacity: i === current ? 1 : 0 }}
            loading="lazy"
          />
        ))}
      </div>
    );
  }

  if (galleryUrls.length === 1) {
    return <img src={galleryUrls[0]} alt={alt} className="rotating-image__single" loading="lazy" />;
  }

  return <EventFallbackPattern seed={seed} />;
};

export default RotatingImage;
